import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Calendar, Building2, Download, List, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { companyPaymentsAPI, companiesAPI, serversAPI, settingsAPI, customersAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';

const RecurringPaymentsAnalysis = () => {
  const { t } = useTranslation();
  const [companyPayments, setCompanyPayments] = useState([]);
  const [servers, setServers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('TRY');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterPackageType, setFilterPackageType] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null); // null = tüm aylar, 1-12 = seçili ay
  const [includeServers, setIncludeServers] = useState(true); // Sunucu maliyetlerini dahil et
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });
  const [analysisView, setAnalysisView] = useState('monthly'); // 'monthly', 'company', 'customer', 'package_type', 'monthly-detail'

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
    fetchCompanies();
    fetchServers();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExchangeRates = async () => {
    try {
      const settings = await settingsAPI.getAll();
      setExchangeRates({
        USD: parseFloat(settings.exchange_rate_usd) || 1,
        EUR: parseFloat(settings.exchange_rate_eur) || 1,
        GBP: parseFloat(settings.exchange_rate_gbp) || 1,
        GOLD: parseFloat(settings.exchange_rate_gold) || 1
      });
    } catch (error) {
      console.error('Exchange rates fetch error:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Companies fetch error:', error);
    }
  };

  const fetchServers = async () => {
    try {
      const data = await serversAPI.getAll();
      setServers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Servers fetch error:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await customersAPI.getAll();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Customers fetch error:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await companyPaymentsAPI.getAll();
      setCompanyPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error(t('recurringPaymentsAnalysis.fetchError') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  // Para birimine göre dönüştür
  const convertToCurrency = (amount, fromCurrency) => {
    if (!amount || amount === 0) return 0;
    const amountNum = parseFloat(amount) || 0;
    
    if (fromCurrency === selectedCurrency) {
      return amountNum;
    }
    
    // Önce TL'ye çevir
    let amountInTRY = amountNum;
    if (fromCurrency !== 'TRY') {
      const rate = exchangeRates[fromCurrency] || 1;
      amountInTRY = amountNum * rate;
    }
    
    // Sonra seçilen para birimine çevir
    if (selectedCurrency === 'TRY') {
      return amountInTRY;
    } else {
      const rate = exchangeRates[selectedCurrency] || 1;
      return amountInTRY / rate;
    }
  };

  // Para birimi sembolü
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'GOLD': t('currency.gold')
    };
    return symbols[currency] || currency;
  };

  // Filtrelenmiş ödemeler
  const filteredPayments = companyPayments.filter(payment => {
    const matchesCompany = !filterCompanyId || payment.company_id === parseInt(filterCompanyId);
    const matchesPackageType = !filterPackageType || payment.package_type === filterPackageType;
    const isActive = payment.is_active !== false && payment.status !== 'cancelled';
    return matchesCompany && matchesPackageType && isActive;
  });

  // XLSX Export fonksiyonu
  const handleExport = () => {
    const dataToExport = filteredPayments.map(payment => ({
      [t('recurringPaymentsAnalysis.company')]: payment.company_name || '',
      [t('recurringPaymentsAnalysis.packageName')]: payment.package_name || payment.payment_type || '',
      [t('recurringPaymentsAnalysis.packageType')]: getPackageTypeText(payment.package_type || payment.payment_type || 'other'),
      [t('recurringPaymentsAnalysis.domain')]: payment.domain_name || '',
      [t('recurringPaymentsAnalysis.purchasePrice')]: payment.purchase_price || payment.amount || 0,
      [t('recurringPaymentsAnalysis.purchaseCurrency')]: payment.purchase_currency || payment.currency || 'TRY',
      [t('recurringPaymentsAnalysis.salePrice')]: payment.sale_price || 0,
      [t('recurringPaymentsAnalysis.saleCurrency')]: payment.sale_currency || 'TRY',
      [t('recurringPaymentsAnalysis.frequency')]: payment.frequency === 'monthly' ? t('common.monthly') : payment.frequency === 'yearly' ? t('common.yearly') : t('common.oneTime'),
      [t('recurringPaymentsAnalysis.startDate')]: payment.start_date ? new Date(payment.start_date).toLocaleDateString('tr-TR') : '',
      [t('recurringPaymentsAnalysis.nextPayment')]: payment.next_payment_date ? new Date(payment.next_payment_date).toLocaleDateString('tr-TR') : '',
      [t('recurringPaymentsAnalysis.paymentProbability')]: payment.payment_probability || 100,
      [t('recurringPaymentsAnalysis.monthlyPurchaseTRY')]: convertToCurrency(payment.purchase_price || payment.amount || 0, payment.purchase_currency || payment.currency || 'TRY') * (payment.frequency === 'monthly' ? 1 : payment.frequency === 'yearly' ? 1/12 : 0),
      [t('recurringPaymentsAnalysis.monthlySaleTRY')]: convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * (payment.frequency === 'monthly' ? 1 : payment.frequency === 'yearly' ? 1/12 : 0),
      [t('recurringPaymentsAnalysis.yearlyPurchaseTRY')]: convertToCurrency(payment.purchase_price || payment.amount || 0, payment.purchase_currency || payment.currency || 'TRY') * (payment.frequency === 'monthly' ? 12 : payment.frequency === 'yearly' ? 1 : 0),
      [t('recurringPaymentsAnalysis.yearlySaleTRY')]: convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * (payment.frequency === 'monthly' ? 12 : payment.frequency === 'yearly' ? 1 : 0)
    }));

    // XLSX oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Para Analizi');
    
    // Dosyayı indir
    XLSX.writeFile(wb, `para_analizi_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success('Para analizi başarıyla export edildi');
  };

  // Aylık analiz
  const getMonthlyAnalysis = () => {
    const months = [
      t('calendar.january'), t('calendar.february'), t('calendar.march'), t('calendar.april'), t('calendar.may'), t('calendar.june'),
      t('calendar.july'), t('calendar.august'), t('calendar.september'), t('calendar.october'), t('calendar.november'), t('calendar.december')
    ];

    const monthlyData = months.map((month, index) => {
      const monthNumber = index + 1;
      let totalPurchase = 0;
      let totalSale = 0;
      let paymentCount = 0;
      const monthPayments = []; // Bu ayın ödemelerini sakla

      filteredPayments.forEach(payment => {
        if (payment.frequency === 'monthly') {
          // Aylık ödemeler - her ay tekrarlanır
          const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
          const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
          totalPurchase += purchase;
          totalSale += sale;
          paymentCount++;
          monthPayments.push({
            ...payment,
            purchaseAmount: purchase,
            saleAmount: sale,
            profitAmount: sale - purchase,
            type: 'payment'
          });
        } else if (payment.frequency === 'yearly') {
          // Yıllık ödemeler - sadece ödeme ayında
          let paymentDate = null;
          
          if (payment.start_date) {
            paymentDate = new Date(payment.start_date);
          } else if (payment.next_payment_date) {
            paymentDate = new Date(payment.next_payment_date);
          }
          
          if (paymentDate) {
            // Yıllık ödeme tarihinin ayını al
            const paymentMonth = paymentDate.getMonth() + 1;
            
            // Eğer bu ay yıllık ödeme ayı ise
            if (paymentMonth === monthNumber) {
              const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
              const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
              totalPurchase += purchase;
              totalSale += sale;
              paymentCount++;
              monthPayments.push({
                ...payment,
                purchaseAmount: purchase,
                saleAmount: sale,
                profitAmount: sale - purchase,
                type: 'payment'
              });
            }
          }
        }
      });

      // Sunucu maliyetlerini ekle
      if (includeServers) {
        servers.forEach(server => {
          if (server.cost && server.status === 'active') {
            const serverCost = convertToCurrency(server.cost, server.currency || 'TRY');
            
            if (server.cost_type === 'monthly') {
              // Aylık sunucu maliyetleri - her ay
              totalPurchase += serverCost;
              paymentCount++;
              monthPayments.push({
                id: `server-${server.id}`,
                server_id: server.id,
                name: server.name,
                company_name: null,
                package_name: server.name,
                package_type: 'server',
                frequency: 'monthly',
                purchaseAmount: serverCost,
                saleAmount: 0,
                profitAmount: -serverCost,
                type: 'server'
              });
            } else if (server.cost_type === 'yearly') {
              // Yıllık sunucu maliyetleri - sadece ödeme ayında
              let serverPaymentDate = null;
              
              if (server.expiry_date) {
                // Bitiş tarihinden geriye doğru 1 yıl ekle
                const expiryDate = new Date(server.expiry_date);
                expiryDate.setFullYear(expiryDate.getFullYear() - 1);
                serverPaymentDate = expiryDate;
              } else if (server.created_at) {
                // Oluşturulma tarihini kullan
                serverPaymentDate = new Date(server.created_at);
              }
              
              if (serverPaymentDate) {
                const serverPaymentMonth = serverPaymentDate.getMonth() + 1;
                
                // Eğer bu ay yıllık sunucu ödeme ayı ise
                if (serverPaymentMonth === monthNumber) {
                  totalPurchase += serverCost;
                  paymentCount++;
                  monthPayments.push({
                    id: `server-${server.id}`,
                    server_id: server.id,
                    name: server.name,
                    company_name: null,
                    package_name: server.name,
                    package_type: 'server',
                    frequency: 'yearly',
                    purchaseAmount: serverCost,
                    saleAmount: 0,
                    profitAmount: -serverCost,
                    type: 'server'
                  });
                }
              }
            }
          }
        });
      }

      const profit = totalSale - totalPurchase;
      return {
        month,
        monthNumber,
        totalPurchase,
        totalSale,
        profit,
        paymentCount,
        payments: monthPayments
      };
    });

    return monthlyData;
  };

  // Seçili ayın detaylı ödemeleri
  const getSelectedMonthPayments = () => {
    if (selectedMonth === null) return [];
    const monthlyData = getMonthlyAnalysis();
    const selectedMonthData = monthlyData.find(m => m.monthNumber === selectedMonth);
    return selectedMonthData ? selectedMonthData.payments : [];
  };

  // Müşteriye göre analiz
  const getCustomerAnalysis = () => {
    const customerMap = {};

    // Önce müşterilerin hangi firmalara bağlı olduğunu bul
    const customerCompanyMap = {};
    customers.forEach(customer => {
      if (customer.company_id) {
        if (!customerCompanyMap[customer.id]) {
          customerCompanyMap[customer.id] = [];
        }
        customerCompanyMap[customer.id].push(customer.company_id);
      }
    });

    filteredPayments.forEach(payment => {
      // Payment'in company_id'sine göre müşterileri bul
      const relatedCustomers = customers.filter(c => {
        if (c.company_id === payment.company_id) return true;
        return customerCompanyMap[c.id]?.includes(payment.company_id);
      });

      if (relatedCustomers.length === 0) {
        // Müşteri bulunamadı
        const key = 'no-customer';
        if (!customerMap[key]) {
          customerMap[key] = {
            customerId: null,
            customerName: t('recurringPaymentsAnalysis.unassignedCustomer'),
            totalPurchase: 0,
            totalSale: 0,
            paymentCount: 0
          };
        }
        
        if (payment.frequency === 'monthly') {
          const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY') * 12;
          const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * 12;
          customerMap[key].totalPurchase += purchase;
          customerMap[key].totalSale += sale;
          customerMap[key].paymentCount += 12;
        } else if (payment.frequency === 'yearly') {
          const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
          const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
          customerMap[key].totalPurchase += purchase;
          customerMap[key].totalSale += sale;
          customerMap[key].paymentCount += 1;
        }
      } else {
        // Her müşteri için ekle
        relatedCustomers.forEach(customer => {
          const customerId = customer.id;
          const customerName = customer.full_name || customer.name || t('recurringPaymentsAnalysis.unknownCustomer');
          
          if (!customerMap[customerId]) {
            customerMap[customerId] = {
              customerId,
              customerName,
              totalPurchase: 0,
              totalSale: 0,
              paymentCount: 0
            };
          }

          if (payment.frequency === 'monthly') {
            const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY') * 12;
            const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * 12;
            customerMap[customerId].totalPurchase += purchase;
            customerMap[customerId].totalSale += sale;
            customerMap[customerId].paymentCount += 12;
          } else if (payment.frequency === 'yearly') {
            const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
            const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
            customerMap[customerId].totalPurchase += purchase;
            customerMap[customerId].totalSale += sale;
            customerMap[customerId].paymentCount += 1;
          }
        });
      }
    });

    return Object.values(customerMap).map(customer => ({
      ...customer,
      profit: customer.totalSale - customer.totalPurchase
    })).sort((a, b) => {
      // Önce isme göre sırala (A-Z)
      const nameCompare = (a.customerName || '').localeCompare(b.customerName || '', 'tr');
      if (nameCompare !== 0) return nameCompare;
      return b.totalSale - a.totalSale;
    });
  };

  // Firmaya göre analiz
  const getCompanyAnalysis = () => {
    const companyMap = {};

    filteredPayments.forEach(payment => {
      const companyId = payment.company_id;
      const companyName = payment.company_name || t('recurringPaymentsAnalysis.unknownCompany');
      
      if (!companyMap[companyId]) {
        companyMap[companyId] = {
          companyId,
          companyName,
          totalPurchase: 0,
          totalSale: 0,
          paymentCount: 0
        };
      }

      // Aylık ödemeler için 12 ay
      if (payment.frequency === 'monthly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY') * 12;
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * 12;
        companyMap[companyId].totalPurchase += purchase;
        companyMap[companyId].totalSale += sale;
        companyMap[companyId].paymentCount += 12;
      } else if (payment.frequency === 'yearly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
        companyMap[companyId].totalPurchase += purchase;
        companyMap[companyId].totalSale += sale;
        companyMap[companyId].paymentCount += 1;
      }
    });

    // Sunucu maliyetlerini ekle (şirket bazlı değil, genel)
    if (includeServers) {
      if (!companyMap['servers']) {
        companyMap['servers'] = {
          companyId: 'servers',
          companyName: t('recurringPaymentsAnalysis.serverCosts'),
          totalPurchase: 0,
          totalSale: 0,
          paymentCount: 0
        };
      }

      servers.forEach(server => {
        if (server.cost && server.status === 'active') {
          const serverCost = convertToCurrency(server.cost, server.currency || 'TRY');
          
          if (server.cost_type === 'monthly') {
            companyMap['servers'].totalPurchase += serverCost * 12;
            companyMap['servers'].paymentCount += 12;
          } else if (server.cost_type === 'yearly') {
            companyMap['servers'].totalPurchase += serverCost;
            companyMap['servers'].paymentCount += 1;
          }
        }
      });
    }

    return Object.values(companyMap).map(company => ({
      ...company,
      profit: company.totalSale - company.totalPurchase
    })).sort((a, b) => {
      // Önce isme göre sırala (A-Z)
      const nameCompare = (a.companyName || '').localeCompare(b.companyName || '', 'tr');
      if (nameCompare !== 0) return nameCompare;
      // İsimler aynıysa satış tutarına göre sırala
      return b.totalSale - a.totalSale;
    });
  };

  // Paket tipine göre analiz
  const getPackageTypeAnalysis = () => {
    const packageTypeMap = {};

    filteredPayments.forEach(payment => {
      const packageType = payment.package_type || 'other';
      const packageTypeText = getPackageTypeText(packageType);
      
      if (!packageTypeMap[packageType]) {
        packageTypeMap[packageType] = {
          packageType,
          packageTypeText,
          totalPurchase: 0,
          totalSale: 0,
          paymentCount: 0
        };
      }

      // Aylık ödemeler için 12 ay
      if (payment.frequency === 'monthly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY') * 12;
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY') * 12;
        packageTypeMap[packageType].totalPurchase += purchase;
        packageTypeMap[packageType].totalSale += sale;
        packageTypeMap[packageType].paymentCount += 12;
      } else if (payment.frequency === 'yearly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
        packageTypeMap[packageType].totalPurchase += purchase;
        packageTypeMap[packageType].totalSale += sale;
        packageTypeMap[packageType].paymentCount += 1;
      }
    });

    // Sunucu maliyetlerini ekle
    if (includeServers) {
      if (!packageTypeMap['server']) {
        packageTypeMap['server'] = {
          packageType: 'server',
          packageTypeText: t('recurringPaymentsAnalysis.server'),
          totalPurchase: 0,
          totalSale: 0,
          paymentCount: 0
        };
      }

      servers.forEach(server => {
        if (server.cost && server.status === 'active') {
          const serverCost = convertToCurrency(server.cost, server.currency || 'TRY');
          
          if (server.cost_type === 'monthly') {
            packageTypeMap['server'].totalPurchase += serverCost * 12;
            packageTypeMap['server'].paymentCount += 12;
          } else if (server.cost_type === 'yearly') {
            packageTypeMap['server'].totalPurchase += serverCost;
            packageTypeMap['server'].paymentCount += 1;
          }
        }
      });
    }

    return Object.values(packageTypeMap).map(pkg => ({
      ...pkg,
      profit: pkg.totalSale - pkg.totalPurchase
    })).sort((a, b) => {
      // Önce paket tipine göre sırala (A-Z)
      const nameCompare = (a.packageTypeText || '').localeCompare(b.packageTypeText || '', 'tr');
      if (nameCompare !== 0) return nameCompare;
      return b.totalSale - a.totalSale;
    });
  };

  const getPackageTypeText = (type) => {
    const types = {
      'domain': t('common.typeDomain'),
      'hosting': t('common.typeHosting'),
      'ssl': t('common.typeSsl'),
      'email': t('common.typeEmail'),
      'reklam': t('common.typeReklam'),
      'danismanlik': t('common.typeDanismanlik'),
      'other': t('common.typeOther')
    };
    return types[type] || type;
  };

  // Toplam istatistikler
  const getTotalStats = () => {
    let totalPurchase = 0;
    let totalSale = 0;
    let totalPayments = 0;

    filteredPayments.forEach(payment => {
      if (payment.frequency === 'monthly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY') * 12;
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
        totalPurchase += purchase;
        totalSale += sale;
        totalPayments += 12;
      } else if (payment.frequency === 'yearly') {
        const purchase = convertToCurrency(payment.purchase_price || payment.amount, payment.purchase_currency || payment.currency || 'TRY');
        const sale = convertToCurrency(payment.sale_price || 0, payment.sale_currency || 'TRY');
        totalPurchase += purchase;
        totalSale += sale;
        totalPayments += 1;
      }
    });

    // Sunucu maliyetlerini ekle
    if (includeServers) {
      servers.forEach(server => {
        if (server.cost && server.status === 'active') {
          const serverCost = convertToCurrency(server.cost, server.currency || 'TRY');
          
          if (server.cost_type === 'monthly') {
            totalPurchase += serverCost * 12;
            totalPayments += 12;
          } else if (server.cost_type === 'yearly') {
            totalPurchase += serverCost;
            totalPayments += 1;
          }
        }
      });
    }

    return {
      totalPurchase,
      totalSale,
      totalProfit: totalSale - totalPurchase,
      totalPayments
    };
  };

  const monthlyData = getMonthlyAnalysis();
  const companyData = getCompanyAnalysis();
  const customerData = getCustomerAnalysis();
  const packageTypeData = getPackageTypeAnalysis();
  const totalStats = getTotalStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 bg-gray-50 dark:bg-gray-900 min-h-full">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('recurringPaymentsAnalysis.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('recurringPaymentsAnalysis.subtitle')}</p>
          </div>
          <button 
            onClick={handleExport}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <Download className="h-5 w-5" />
            <span className="font-medium">Export</span>
          </button>
        </div>
      </div>

      {/* Filtreler ve Kontroller */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurringPaymentsAnalysis.currency')}
            </label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="TRY">{t('currency.try')}</option>
              <option value="USD">{t('currency.usd')}</option>
              <option value="EUR">{t('currency.eur')}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurringPaymentsAnalysis.year')}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value));
                setSelectedMonth(null); // Yıl değişince ay seçimini sıfırla
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {[selectedYear - 1, selectedYear, selectedYear + 1].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurringPaymentsAnalysis.monthSelection')}
            </label>
            <select
              value={selectedMonth || ''}
              onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('recurringPaymentsAnalysis.allMonths')}</option>
              {[
                t('calendar.january'), t('calendar.february'), t('calendar.march'), t('calendar.april'), t('calendar.may'), t('calendar.june'),
                t('calendar.july'), t('calendar.august'), t('calendar.september'), t('calendar.october'), t('calendar.november'), t('calendar.december')
              ].map((month, index) => (
                <option key={index + 1} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurringPaymentsAnalysis.companyFilter')}
            </label>
            <select
              value={filterCompanyId}
              onChange={(e) => setFilterCompanyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('recurringPaymentsAnalysis.allCompanies')}</option>
              {[...companies].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')).map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurringPaymentsAnalysis.packageType')}
            </label>
            <select
              value={filterPackageType}
              onChange={(e) => setFilterPackageType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('recurringPaymentsAnalysis.allTypes')}</option>
              <option value="domain">{t('common.typeDomain')}</option>
              <option value="hosting">{t('common.typeHosting')}</option>
              <option value="ssl">{t('common.typeSsl')}</option>
              <option value="email">{t('common.typeEmail')}</option>
              <option value="reklam">{t('common.typeReklam')}</option>
              <option value="danismanlik">{t('common.typeDanismanlik')}</option>
              <option value="other">{t('common.typeOther')}</option>
            </select>
          </div>
        </div>

        {/* Sunucu Maliyetleri Dahil Et */}
        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="includeServers"
            checked={includeServers}
            onChange={(e) => setIncludeServers(e.target.checked)}
            className="w-4 h-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
          />
          <label htmlFor="includeServers" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            {t('recurringPaymentsAnalysis.includeServerCosts')}
          </label>
        </div>

        {/* Analiz Görünümü Seçimi */}
        <div className="mt-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setAnalysisView('monthly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              analysisView === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Calendar className="inline h-4 w-4 mr-2" />
            {t('recurringPaymentsAnalysis.monthlyAnalysis')}
          </button>
          <button
            onClick={() => setAnalysisView('company')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              analysisView === 'company'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Building2 className="inline h-4 w-4 mr-2" />
            {t('recurringPaymentsAnalysis.byCompany')}
          </button>
          <button
            onClick={() => setAnalysisView('customer')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              analysisView === 'customer'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Users className="inline h-4 w-4 mr-2" />
            {t('recurringPaymentsAnalysis.byCustomer')}
          </button>
          <button
            onClick={() => setAnalysisView('package_type')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              analysisView === 'package_type'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <BarChart3 className="inline h-4 w-4 mr-2" />
            {t('recurringPaymentsAnalysis.byPackageType')}
          </button>
          {selectedMonth !== null && (
            <button
              onClick={() => setAnalysisView('monthly-detail')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                analysisView === 'monthly-detail'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <List className="inline h-4 w-4 mr-2" />
              {t('recurringPaymentsAnalysis.selectedMonthDetail')}
            </button>
          )}
        </div>
      </div>

      {/* Toplam İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('recurringPaymentsAnalysis.totalPurchase')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('recurringPaymentsAnalysis.totalSale')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('recurringPaymentsAnalysis.totalProfit')}</p>
              <p className={`text-2xl font-bold mt-1 ${
                totalStats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {getCurrencySymbol(selectedCurrency)} {totalStats.totalProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('recurringPaymentsAnalysis.totalPayments')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {totalStats.totalPayments}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Aylık Analiz Tablosu ve Grafik */}
      {analysisView === 'monthly' && (
        <>
          {/* Grafik */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{t('recurringPaymentsAnalysis.monthlyChart')} ({selectedYear})</h3>
            <div className="relative" style={{ height: '320px' }}>
              <div className="grid grid-cols-12 items-end h-full px-2" style={{ gap: '4px' }}>
                {monthlyData.map((month, index) => {
                  const maxValue = Math.max(...monthlyData.map(m => Math.max(m.totalSale, m.totalPurchase)));
                  const saleHeight = maxValue > 0 ? (month.totalSale / maxValue) * 100 : 0;
                  const purchaseHeight = maxValue > 0 ? (month.totalPurchase / maxValue) * 100 : 0;
                  const isSelected = selectedMonth === month.monthNumber;
                  
                  return (
                    <div 
                      key={index} 
                      className="flex flex-col items-center cursor-pointer group relative h-full border-r-2 border-gray-300 dark:border-gray-600 last:border-r-0 px-2"
                      onClick={() => setSelectedMonth(selectedMonth === month.monthNumber ? null : month.monthNumber)}
                    >
                      <div className="w-full flex items-end justify-center gap-1 h-full" style={{ paddingBottom: '60px' }}>
                        <div className="flex flex-col items-center justify-end w-1/2 h-full relative">
                          {saleHeight > 0 && (
                            <div 
                              className="absolute left-1/2 transform -translate-x-1/2 text-xs font-medium text-green-700 dark:text-green-400 whitespace-nowrap z-10 bg-white dark:bg-gray-800 px-1 rounded shadow-sm"
                              style={{ bottom: `calc(${saleHeight}% + 4px)` }}
                            >
                              {getCurrencySymbol(selectedCurrency)}{month.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </div>
                          )}
                          <div 
                            className="w-full bg-green-500 rounded-t transition-all hover:bg-green-600 relative"
                            style={{ 
                              height: `${saleHeight}%`,
                              minHeight: saleHeight > 0 ? '2px' : '0px'
                            }}
                            title={`${t('recurringPaymentsAnalysis.sale')}: ${getCurrencySymbol(selectedCurrency)}${month.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          />
                        </div>
                        <div className="flex flex-col items-center justify-end w-1/2 h-full relative">
                          {purchaseHeight > 0 && (
                            <div 
                              className="absolute left-1/2 transform -translate-x-1/2 text-xs font-medium text-red-700 dark:text-red-400 whitespace-nowrap z-10 bg-white dark:bg-gray-800 px-1 rounded shadow-sm"
                              style={{ bottom: `calc(${purchaseHeight}% + 4px)` }}
                            >
                              {getCurrencySymbol(selectedCurrency)}{month.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </div>
                          )}
                          <div 
                            className="w-full bg-red-500 rounded-t transition-all hover:bg-red-600 relative"
                            style={{ 
                              height: `${purchaseHeight}%`,
                              minHeight: purchaseHeight > 0 ? '2px' : '0px'
                            }}
                            title={`${t('recurringPaymentsAnalysis.purchase')}: ${getCurrencySymbol(selectedCurrency)}${month.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          />
                        </div>
                      </div>
                      <div className={`absolute bottom-0 text-xs font-medium text-center w-full ${isSelected ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-600 dark:text-gray-400'}`}>
                        {month.month.substring(0, 3)}
                      </div>
                      {isSelected && (
                        <div className="absolute -bottom-5 text-xs text-blue-600 dark:text-blue-400 font-semibold">✓</div>
                      )}
                      {/* Hover tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 pointer-events-none">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                          <div>{t('recurringPaymentsAnalysis.sale')}: {getCurrencySymbol(selectedCurrency)}{month.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div>{t('recurringPaymentsAnalysis.purchase')}: {getCurrencySymbol(selectedCurrency)}{month.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div>{t('recurringPaymentsAnalysis.profit')}: {getCurrencySymbol(selectedCurrency)}{month.profit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('recurringPaymentsAnalysis.sale')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('recurringPaymentsAnalysis.purchase')}</span>
              </div>
            </div>
          </div>

          {/* Tablo - Compact */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('recurringPaymentsAnalysis.monthlyTable')} ({selectedYear})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                      {t('recurringPaymentsAnalysis.month')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                      {t('recurringPaymentsAnalysis.purchase')} / {t('recurringPaymentsAnalysis.sale')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                      {t('recurringPaymentsAnalysis.profit')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                      {t('recurringPaymentsAnalysis.paymentCount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {monthlyData.map((month, index) => {
                    const isSelected = selectedMonth === month.monthNumber;
                    return (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                        onClick={() => {
                          setSelectedMonth(isSelected ? null : month.monthNumber);
                          setAnalysisView('monthly-detail');
                        }}
                      >
                        <td className="px-3 py-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{month.month}</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-sm text-red-600 dark:text-red-400">{getCurrencySymbol(selectedCurrency)}{month.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            <span className="text-sm text-green-600 dark:text-green-400">{getCurrencySymbol(selectedCurrency)}{month.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className={`text-sm font-semibold ${
                            month.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {getCurrencySymbol(selectedCurrency)}{month.profit.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="text-sm text-gray-500 dark:text-gray-400">{month.paymentCount}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Seçili Ay Detayı - Compact View */}
      {analysisView === 'monthly-detail' && selectedMonth !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {monthlyData.find(m => m.monthNumber === selectedMonth)?.month} {selectedYear} - {t('recurringPaymentsAnalysis.detailedPayments')}
            </h3>
            <button
              onClick={() => {
                setSelectedMonth(null);
                setAnalysisView('monthly');
              }}
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              {t('recurringPaymentsAnalysis.goBack')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.company')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.package')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.purchase')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.sale')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.profitLoss')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {getSelectedMonthPayments().length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-3 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                      {t('recurringPaymentsAnalysis.noPaymentsForMonth')}
                    </td>
                  </tr>
                ) : (
                  getSelectedMonthPayments().map((payment, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {payment.company_name || (payment.type === 'server' ? t('recurringPaymentsAnalysis.server') : 'N/A')}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {payment.package_name || payment.name}
                          </span>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${
                              payment.type === 'server'
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : payment.package_type === 'domain'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : payment.package_type === 'hosting'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                            }`}>
                              {payment.type === 'server' ? t('recurringPaymentsAnalysis.server') : getPackageTypeText(payment.package_type)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              • {payment.frequency === 'monthly' ? t('common.monthly') : payment.frequency === 'yearly' ? t('common.yearly') : t('common.oneTime')}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {getCurrencySymbol(selectedCurrency)}{payment.purchaseAmount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {payment.saleAmount > 0 ? (
                            <>{getCurrencySymbol(selectedCurrency)}{payment.saleAmount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className={`text-sm font-semibold ${
                          payment.profitAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {getCurrencySymbol(selectedCurrency)}{payment.profitAmount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Firmaya Göre Analiz - Compact */}
      {analysisView === 'company' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('recurringPaymentsAnalysis.byCompanyAnalysis')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.company')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.purchase')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.sale')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.profit')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.paymentCount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {companyData.map((company, index) => {
                  // Bu firmaya ait domainleri bul
                  const companyDomains = filteredPayments.filter(p => p.company_id === company.companyId && p.domain_name);
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{company.companyName}</div>
                        {companyDomains.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {companyDomains.slice(0, 3).map((payment, idx) => (
                              <a
                                key={idx}
                                href={`https://${payment.domain_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {payment.domain_name}
                              </a>
                            ))}
                            {companyDomains.length > 3 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">+{companyDomains.length - 3} daha</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="text-sm text-red-600 dark:text-red-400">{getCurrencySymbol(selectedCurrency)}{company.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="text-sm text-green-600 dark:text-green-400">{getCurrencySymbol(selectedCurrency)}{company.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className={`text-sm font-semibold ${
                          company.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {getCurrencySymbol(selectedCurrency)}{company.profit.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{company.paymentCount}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Müşteriye Göre Analiz - Compact */}
      {analysisView === 'customer' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('recurringPaymentsAnalysis.byCustomerAnalysis')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.customer')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.purchase')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.sale')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.profit')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.paymentCount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {customerData.map((customer, index) => (
                  <tr key={customer.customerId || 'no-customer'} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{customer.customerName}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm text-red-600 dark:text-red-400">{getCurrencySymbol(selectedCurrency)}{customer.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm text-green-600 dark:text-green-400">{getCurrencySymbol(selectedCurrency)}{customer.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className={`text-sm font-semibold ${
                        customer.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {getCurrencySymbol(selectedCurrency)}{customer.profit.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="text-sm text-gray-500 dark:text-gray-400">{customer.paymentCount}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Paket Tipine Göre Analiz - Compact */}
      {analysisView === 'package_type' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('recurringPaymentsAnalysis.byPackageTypeAnalysis')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.packageType')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.purchase')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.sale')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.profit')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    {t('recurringPaymentsAnalysis.paymentCount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {packageTypeData.map((pkg, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{pkg.packageTypeText}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm text-red-600 dark:text-red-400">{getCurrencySymbol(selectedCurrency)}{pkg.totalPurchase.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-sm text-green-600 dark:text-green-400">{getCurrencySymbol(selectedCurrency)}{pkg.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className={`text-sm font-semibold ${
                        pkg.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {getCurrencySymbol(selectedCurrency)}{pkg.profit.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="text-sm text-gray-500 dark:text-gray-400">{pkg.paymentCount}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringPaymentsAnalysis;

